import { PayoutStatus } from "@/Apollo/schema";
import { InfoLabelProps } from "@/ui/cva/InfoLabel";

export const SALES_FETCH_LIMIT = 200;
export const SALES_PAGE_SIZE = 15;

export const SELLER_SALE_STATUS = {
  pending: "pending",
  actionNeeded: "action_needed",
  paid: "paid",
  canceled: "canceled",
} as const;

export type SellerSaleStatus =
  (typeof SELLER_SALE_STATUS)[keyof typeof SELLER_SALE_STATUS];

export type SalesStatusFilterOption = {
  id: SellerSaleStatus;
  labelKey: string;
  defaultLabel: string;
};

/** The explicit `Alles` row of the multi-select filter (A42); it is not a status. */
export const SALES_STATUS_FILTER_ALL_OPTION = {
  labelKey: "account-payment:sales-filter-all",
  defaultLabel: "Alles",
};

export const SALES_STATUS_FILTER_OPTIONS: SalesStatusFilterOption[] = [
  {
    id: SELLER_SALE_STATUS.pending,
    labelKey: "account-payment:sales-status-pending",
    defaultLabel: "In afwachting",
  },
  {
    id: SELLER_SALE_STATUS.actionNeeded,
    labelKey: "account-payment:sales-status-action-needed",
    defaultLabel: "Actie nodig",
  },
  {
    id: SELLER_SALE_STATUS.paid,
    labelKey: "account-payment:sales-status-paid",
    defaultLabel: "Uitbetaald",
  },
  {
    id: SELLER_SALE_STATUS.canceled,
    labelKey: "account-payment:sales-status-canceled",
    defaultLabel: "Geannuleerd",
  },
];

/** Module-level so the URL filter hook keeps a stable parser. */
export const SALES_STATUS_FILTER_IDS: readonly SellerSaleStatus[] =
  SALES_STATUS_FILTER_OPTIONS.map(option => option.id);

export const PENDING_PAYOUT_STATUSES: PayoutStatus[] = [
  PayoutStatus.WaitingForConfirmation,
  PayoutStatus.Pending,
  PayoutStatus.Delivered,
  PayoutStatus.PendingTransfer,
];

export const ACTION_NEEDED_PAYOUT_STATUSES: PayoutStatus[] = [
  PayoutStatus.Failed,
  PayoutStatus.Disputed,
];

export const PAID_PAYOUT_STATUSES: PayoutStatus[] = [
  PayoutStatus.TransferInTransit,
  PayoutStatus.InTransit,
  PayoutStatus.Succeeded,
  PayoutStatus.Completed,
];

export const SALES_STATUS_PILL: Record<
  SellerSaleStatus,
  { labelKey: string; defaultLabel: string; color: InfoLabelProps["color"] }
> = {
  [SELLER_SALE_STATUS.pending]: {
    labelKey: "account-payment:sales-status-pending",
    defaultLabel: "In afwachting",
    color: "curious",
  },
  [SELLER_SALE_STATUS.actionNeeded]: {
    labelKey: "account-payment:sales-status-action-needed",
    defaultLabel: "Actie nodig",
    color: "attention",
  },
  [SELLER_SALE_STATUS.paid]: {
    labelKey: "account-payment:sales-status-paid",
    defaultLabel: "Uitbetaald",
    color: "brand",
  },
  [SELLER_SALE_STATUS.canceled]: {
    labelKey: "account-payment:sales-status-canceled",
    defaultLabel: "Geannuleerd",
    color: "attention",
  },
};

/**
 * Dutch source copy for WHO-5450. English is the Lokalise upload pair.
 * Keep this the single list of new user-facing strings so tests can enforce
 * the wording rules (no em dashes, no "verstuurd", no "netto", Stripe named
 * as betaalserviceprovider).
 */
export const SALES_COPY = {
  statusPending: { nl: "In afwachting", en: "Pending" },
  statusActionNeeded: { nl: "Actie nodig", en: "Action needed" },
  statusPaid: { nl: "Uitbetaald", en: "Paid out" },
  statusCanceled: { nl: "Geannuleerd", en: "Cancelled" },
  explainPending: {
    nl: "De koper heeft nog niet bevestigd dat het item is ontvangen. Zodra de bestelling is afgerond, maakt Whoppah de uitbetaling over.",
    en: "The buyer has not yet confirmed that the item was received. Once the order is completed, Whoppah will send the payout.",
  },
  explainPendingDelivered: {
    nl: "Het item is bezorgd. De koper heeft drie dagen om te bevestigen dat alles in goede orde is; daarna wordt de bestelling afgerond en start de uitbetaling.",
    en: "The item has been delivered. The buyer has three days to confirm everything is in order; after that the order is completed and the payout starts.",
  },
  explainActionNeeded: {
    nl: "Je uitbetaling staat klaar, maar je account is nog niet volledig geverifieerd. Rond de verificatie af bij je <settings>betaalinstellingen</settings>.",
    en: "Your payout is ready, but your account is not fully verified yet. Complete verification in your <settings>payment settings</settings>.",
  },
  explainPaid: {
    nl: "Het bedrag is uitbetaald door betaalserviceprovider Stripe. Het kan nog enkele dagen duren voordat het op je bankrekening staat. Staat het er daarna nog niet? Controleer de bankrekening in je <settings>betaalinstellingen</settings> of neem <support>contact op met support</support>.",
    en: "The amount was paid out by payment service provider Stripe. It can take a few more days to arrive in your bank account. Still not there after that? Check the bank account in your <settings>payment settings</settings> or <support>contact support</support>.",
  },
  tooltipPending: {
    nl: "Het item is onderweg of de koper moet nog bevestigen dat het is ontvangen.",
    en: "The item is on its way, or the buyer still needs to confirm it was received.",
  },
  tooltipActionNeeded: {
    nl: "Deze verkoop heeft je aandacht nodig voordat de uitbetaling doorgaat.",
    en: "This sale needs your attention before the payout can continue.",
  },
  tooltipPaid: {
    nl: "Het bedrag is uitbetaald door betaalserviceprovider Stripe. Het kan nog enkele dagen duren voordat het op je bankrekening staat.",
    en: "The amount was paid out by payment service provider Stripe. It can take a few more days to arrive in your bank account.",
  },
  blockBanner: {
    nl: "<bold>{{amount}}</bold> staat klaar voor uitbetaling, maar je uitbetalingen zijn geblokkeerd. <settings>Ga naar je betaalinstellingen</settings>",
    en: "<bold>{{amount}}</bold> is ready to be paid out, but your payouts are blocked. <settings>Go to your payment settings</settings>",
  },
  tooltipHeld: {
    nl: "De uitbetaling is geblokkeerd. Ga naar de betaalinstellingen om dit op te lossen.",
    en: "The payout is blocked. Go to your payment settings to resolve this.",
  },
  tooltipDisputed: {
    nl: "De koper heeft een geschil geopend. De uitbetaling volgt zodra het geschil is opgelost.",
    en: "The buyer has opened a dispute. The payout follows once the dispute is resolved.",
  },
  headerPaid: { nl: "Uitbetaald", en: "Paid out" },
  headerPending: { nl: "In afwachting", en: "Pending" },
  afterCommission: {
    nl: "na aftrek van commissie",
    en: "after commission",
  },
  breakdownTitle: {
    nl: "Opbouw van je uitbetaling",
    en: "Breakdown of your payout",
  },
  explainFailed: {
    nl: "De uitbetaling is niet gelukt. Controleer je <settings>betaalinstellingen</settings>. Als die in orde zijn, neem dan <support>contact op met support</support>.",
    en: "The payout didn't go through. Check your <settings>payment settings</settings>. If those are correct, <support>contact support</support>.",
  },
  explainDisputed: {
    nl: "De koper heeft een probleem gemeld met deze bestelling. De uitbetaling gaat pas door als dit is opgelost. Los het samen op in de <chat>chat</chat>, of neem <support>contact op met support</support> als je er niet uit komt.",
    en: "The buyer reported a problem with this order. The payout continues once it's resolved. Sort it out together in the <chat>chat</chat>, or <support>contact support</support> if you can't find a solution.",
  },
  pageTitle: { nl: "Mijn verkopen", en: "My sales" },
  orderNumber: { nl: "Ordernummer", en: "Order number" },
  salePrice: { nl: "Verkoopprijs", en: "Sale price" },
  commission: {
    nl: "Whoppah-commissie ({{pct}}%)",
    en: "Whoppah commission ({{pct}}%)",
  },
  commissionExVat: {
    nl: "Whoppah-commissie ({{pct}}%) exclusief btw",
    en: "Whoppah commission ({{pct}}%) excluding VAT",
  },
  commissionVat: {
    nl: "Btw over commissie (21%)",
    en: "VAT on commission (21%)",
  },
  yourPayout: { nl: "Jouw uitbetaling", en: "Your payout" },
  filterByStatus: { nl: "Filter op status", en: "Filter by status" },
  filterAll: { nl: "Alles", en: "All" },
  searchPlaceholder: { nl: "Zoeken in verkopen", en: "Search sales" },
  emptyBody: {
    nl: "Zodra je iets verkoopt, verschijnt het hier.",
    en: "Once you sell something, it will show up here.",
  },
  columnItem: { nl: "Item", en: "Item" },
  columnStatus: { nl: "Status", en: "Status" },
  columnSold: { nl: "Verkocht", en: "Sold" },
  columnPayout: { nl: "Uitbetaling", en: "Payout" },
  documents: { nl: "Documenten", en: "Documents" },
  download: { nl: "Download overzicht", en: "Download overview" },
  goBack: { nl: "Terug naar mijn verkopen", en: "Back to my sales" },
  soldOn: { nl: "Verkocht op {{date}}", en: "Sold on {{date}}" },
  statusTitle: { nl: "Status", en: "Status" },
  columnSale: { nl: "Verkoop", en: "Sale" },
  columnSoldOn: { nl: "Verkocht op", en: "Sold on" },
  menuDetails: { nl: "Verkoop details", en: "Sale details" },
  downloadUnavailable: {
    nl: "Beschikbaar zodra de verkoop is afgerond",
    en: "Available once the sale is completed",
  },
  chatWithBuyer: { nl: "Chat met de koper", en: "Chat with the buyer" },
  emptyMiss: {
    nl: "We hebben geen verkopen gevonden",
    en: "We found no sales",
  },
  emptyFirstTitle: {
    nl: "Je hebt nog niets verkocht",
    en: "You have not sold anything yet",
  },
  copyOrderNumber: { nl: "Kopieer ordernummer", en: "Copy order number" },
  otherActions: { nl: "Overige acties", en: "Other actions" },
  moreActions: { nl: "Meer acties", en: "More actions" },
  tooltipCanceled: {
    nl: "Deze verkoop is geannuleerd. Er volgt geen uitbetaling.",
    en: "This sale was cancelled. There will be no payout.",
  },
  /** `<ads>` deep-links to this sale's own ad, not the list (WHO-5551). */
  explainCanceled: {
    nl: "Deze verkoop is geannuleerd, dus er volgt geen uitbetaling en je hoeft niets te doen. Wil je het item opnieuw verkopen? Controleer of de advertentie nog live staat. <ads>Bekijk advertentie</ads>",
    en: "This sale was cancelled, so there is no payout and nothing for you to do. Want to sell the item again? Check that the ad is still live. <ads>View ad</ads>",
  },
} as const;

export const SALES_LOKALISE_KEYS: {
  key: string;
  copyKey: keyof typeof SALES_COPY;
}[] = [
  { key: "account-payment:sales-status-pending", copyKey: "statusPending" },
  {
    key: "account-payment:sales-status-action-needed",
    copyKey: "statusActionNeeded",
  },
  { key: "account-payment:sales-status-paid", copyKey: "statusPaid" },
  { key: "account-payment:sales-status-canceled", copyKey: "statusCanceled" },
  { key: "account-payment:sales-explain-pending", copyKey: "explainPending" },
  {
    key: "account-payment:sales-explain-pending-delivered",
    copyKey: "explainPendingDelivered",
  },
  {
    key: "account-payment:sales-explain-action-needed",
    copyKey: "explainActionNeeded",
  },
  { key: "account-payment:sales-explain-paid", copyKey: "explainPaid" },
  { key: "account-payment:sales-tooltip-pending", copyKey: "tooltipPending" },
  {
    key: "account-payment:sales-tooltip-action-needed",
    copyKey: "tooltipActionNeeded",
  },
  { key: "account-payment:sales-tooltip-paid", copyKey: "tooltipPaid" },
  { key: "account-payment:sales-block-banner", copyKey: "blockBanner" },
  { key: "account-payment:sales-tooltip-held", copyKey: "tooltipHeld" },
  {
    key: "account-payment:sales-tooltip-disputed",
    copyKey: "tooltipDisputed",
  },
  { key: "account-payment:sales-header-paid", copyKey: "headerPaid" },
  {
    key: "account-payment:sales-header-pending",
    copyKey: "headerPending",
  },
  { key: "account-payment:sales-explain-failed", copyKey: "explainFailed" },
  {
    key: "account-payment:sales-explain-disputed",
    copyKey: "explainDisputed",
  },
  { key: "account-payment:sales-page-title", copyKey: "pageTitle" },
  {
    key: "account-payment:sales-after-commission",
    copyKey: "afterCommission",
  },
  {
    key: "account-payment:sales-breakdown-title",
    copyKey: "breakdownTitle",
  },
  { key: "account-payment:sales-order-number", copyKey: "orderNumber" },
  { key: "account-payment:sales-sale-price", copyKey: "salePrice" },
  { key: "account-payment:sales-commission", copyKey: "commission" },
  {
    key: "account-payment:sales-commission-ex-vat",
    copyKey: "commissionExVat",
  },
  { key: "account-payment:sales-commission-vat", copyKey: "commissionVat" },
  { key: "account-payment:sales-your-payout", copyKey: "yourPayout" },
  {
    key: "account-payment:sales-filter-by-status",
    copyKey: "filterByStatus",
  },
  { key: "account-payment:sales-filter-all", copyKey: "filterAll" },
  {
    key: "account-payment:sales-search-placeholder",
    copyKey: "searchPlaceholder",
  },
  { key: "account-payment:sales-empty-body", copyKey: "emptyBody" },
  { key: "account-payment:sales-column-item", copyKey: "columnItem" },
  { key: "account-payment:sales-column-status", copyKey: "columnStatus" },
  { key: "account-payment:sales-column-sold", copyKey: "columnSold" },
  { key: "account-payment:sales-column-payout", copyKey: "columnPayout" },
  { key: "account-payment:sales-documents", copyKey: "documents" },
  { key: "account-payment:sales-download", copyKey: "download" },
  { key: "account-payment:sales-go-back", copyKey: "goBack" },
  { key: "account-payment:sales-sold-on", copyKey: "soldOn" },
  { key: "account-payment:sales-status-title", copyKey: "statusTitle" },
  { key: "account-payment:sales-column-sale", copyKey: "columnSale" },
  { key: "account-payment:sales-column-sold-on", copyKey: "columnSoldOn" },
  { key: "account-payment:sales-menu-details", copyKey: "menuDetails" },
  {
    key: "account-payment:sales-download-unavailable",
    copyKey: "downloadUnavailable",
  },
  { key: "account-payment:sales-chat-with-buyer", copyKey: "chatWithBuyer" },
  { key: "account-payment:sales-empty-miss", copyKey: "emptyMiss" },
  {
    key: "account-payment:sales-empty-first-title",
    copyKey: "emptyFirstTitle",
  },
  {
    key: "account-payment:sales-copy-order-number",
    copyKey: "copyOrderNumber",
  },
  { key: "account-payment:sales-other-actions", copyKey: "otherActions" },
  { key: "account-payment:sales-more-actions", copyKey: "moreActions" },
  {
    key: "account-payment:sales-tooltip-canceled",
    copyKey: "tooltipCanceled",
  },
  {
    key: "account-payment:sales-explain-canceled",
    copyKey: "explainCanceled",
  },
];

export type SalesSortColumnId = "Title" | "Created" | "Payout";

export type SalesMenuAction = {
  kind: "details" | "download" | "chat";
  labelKey: string;
  defaultLabel: string;
  disabled?: boolean;
  disabledExplanation?: string;
};

export const getSalesRowMenuActions = (options: {
  hasReceipt: boolean;
  hasThread: boolean;
}): SalesMenuAction[] => {
  const items: SalesMenuAction[] = [
    {
      kind: "details",
      labelKey: "account-payment:sales-menu-details",
      defaultLabel: "Verkoop details",
    },
    {
      kind: "download",
      labelKey: "account-payment:sales-download",
      defaultLabel: "Download overzicht",
      disabled: !options.hasReceipt,
      disabledExplanation: options.hasReceipt
        ? undefined
        : "Beschikbaar zodra de verkoop is afgerond",
    },
  ];

  if (options.hasThread) {
    items.push({
      kind: "chat",
      labelKey: "account-payment:sales-chat-with-buyer",
      defaultLabel: "Chat met de koper",
    });
  }

  return items;
};

export const SALES_SORT_OPTIONS = [
  {
    id: "newest",
    columnId: "Created" as SalesSortColumnId,
    order: "Desc" as const,
    labelKey: "account-ads:mobile-sort-newest",
    defaultLabel: "Nieuwste eerst",
  },
  {
    id: "oldest",
    columnId: "Created" as SalesSortColumnId,
    order: "Asc" as const,
    labelKey: "account-ads:mobile-sort-oldest",
    defaultLabel: "Oudste eerst",
  },
  {
    id: "payout-asc",
    columnId: "Payout" as SalesSortColumnId,
    order: "Asc" as const,
    labelKey: "account-payment:sales-sort-payout-asc",
    defaultLabel: "Uitbetaling: laag naar hoog",
  },
  {
    id: "payout-desc",
    columnId: "Payout" as SalesSortColumnId,
    order: "Desc" as const,
    labelKey: "account-payment:sales-sort-payout-desc",
    defaultLabel: "Uitbetaling: hoog naar laag",
  },
  {
    id: "title-asc",
    columnId: "Title" as SalesSortColumnId,
    order: "Asc" as const,
    labelKey: "account-ads:mobile-sort-title-asc",
    defaultLabel: "Titel: A tot Z",
  },
];
